import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { useCookiesConsentStrings } from './cookies-consent-strings';
import './CookiesConsent.css';

interface CookiesConsentProps {
  onConsent: (accepted: boolean) => void;
}

export default function CookiesConsent({ onConsent }: CookiesConsentProps) {
  const s = useCookiesConsentStrings();
  const [open, setOpen] = useState(false);

  // Function to update Google Tag Manager consent mode with retry mechanism
  // Matches the implementation in cookies-consent.js
  const updateGTMConsent = (accepted: boolean, retryCount = 0, pushEvent = false) => {
    const maxRetries = 50; // Maximum 5 seconds (50 * 100ms)
    const gtmContainer = (window as any).google_tag_manager?.['GTM-MRCZT4H9'];
    
    // Check if GTM is fully ready
    if ((window as any).google_tag_manager && gtmContainer) {
      const gtmDataLayer = gtmContainer.dataLayer;
      const consentValue = accepted ? 'granted' : 'denied';
      
      // Use GTM dataLayer directly (same approach as cookies-consent.js)
      if (gtmDataLayer && typeof gtmDataLayer.set === 'function') {
        gtmDataLayer.set('analytics_storage', consentValue);
        gtmDataLayer.set('ad_storage', consentValue);
      }
      
      // Only push event if explicitly requested (for user actions, not when restoring)
      if (pushEvent && typeof (window as any).dataLayer !== 'undefined') {
        (window as any).dataLayer.push({
          'event': accepted ? 'cookies_accepted' : 'cookies_declined',
          'consent_given': accepted
        });
      }
      
      // Also use gtag function if available (additional method for better compatibility)
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': consentValue,
          'ad_storage': consentValue
        });
      }
    } else if (retryCount < maxRetries) {
      // GTM not ready yet, retry after a delay (preserve pushEvent flag)
      setTimeout(() => updateGTMConsent(accepted, retryCount + 1, pushEvent), 100);
    } else {
      // Max retries reached, try fallback with dataLayer (only if pushEvent is true)
      if (pushEvent && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': accepted ? 'cookies_accepted' : 'cookies_declined',
          'consent_given': accepted,
          'analytics_storage': accepted ? 'granted' : 'denied',
          'ad_storage': accepted ? 'granted' : 'denied'
        });
      }
    }
  };

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = localStorage.getItem('cookiesConsent');
    if (!hasConsent) {
      setOpen(true);
    } else {
      const accepted = hasConsent === 'accepted';
      onConsent(accepted);
      // Update GTM consent mode based on existing consent (no event push for restoration)
      // The function has its own retry mechanism
      updateGTMConsent(accepted, 0, false);
    }
  }, [onConsent]);

  const handleAccept = () => {
    localStorage.setItem('cookiesConsent', 'accepted');
    setOpen(false);
    onConsent(true);
    // Update GTM consent mode and push event (user action)
    updateGTMConsent(true, 0, true);
  };

  const handleDecline = () => {
    localStorage.setItem('cookiesConsent', 'declined');
    setOpen(false);
    onConsent(false);
    // Update GTM consent mode and push event (user action)
    updateGTMConsent(false, 0, true);
    
    // Optionally, you can limit functionality for users who decline cookies
    // For now, we just track their preference
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={(_event, reason) => {
        // MUI v9 removed disableEscapeKeyDown — ignore escape/backdrop so the user must choose.
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') return;
      }}
      className="cookies-consent-dialog"
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1000, // High z-index to ensure it's above everything
      }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: '600px',
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          background: '#01ABAE',
          color: 'white',
          textAlign: 'center',
          fontWeight: 600,
          padding: '20px 24px',
          borderRadius: '12px 12px 0 0',
          fontSize: '18px',
          lineHeight: 1.4,
          margin: 0
        }}
      >
        {s.title}
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '24px !important',
        }}
      >
        <Typography 
          variant="body1" 
          className="cookies-consent-description"
          sx={{
            marginBottom: "16px",
            fontSize: "16px",
            lineHeight: 1.5,
            color: "#333"
          }}
        >
          {s.descriptionIntro}
        </Typography>
        
        <Typography 
          variant="body2" 
          component="ul" 
          className="cookies-consent-list"
          sx={{ 
            pl: 2,
            color: "#555",
            margin: "16px 0",
            '& li': {
              margin: '8px 0 !important',
              lineHeight: 1.5,
              color: '#555'
            }
          }}
        >
          <li>{s.bulletPreferences}</li>
          <li>{s.bulletLoggedIn}</li>
          <li>{s.bulletAnalytics}</li>
          <li>{s.bulletServices}</li>
        </Typography>
        
        <Typography 
          variant="body2" 
          className="cookies-consent-policy"
          sx={{ 
            mt: 2, 
            fontSize: "14px",
            color: "#666",
            lineHeight: 1.5
          }}
        >
          {s.policyPrefix}{' '}
          <a 
            href={s.cookiesPolicyUrl}
            target="_blank" 
            rel="noopener noreferrer" 
            className="cookies-consent-link"
            style={{
              color: '#01ABAE',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            {s.policyLink}
          </a>.
        </Typography>
      </DialogContent>
      <DialogActions 
        className="cookies-consent-actions"
        sx={{ 
          justifyContent: 'space-between', 
          padding: '16px 24px 24px',
          gap: '12px'
        }}
      >
        <Button 
          onClick={handleDecline}
          variant="outlined"
          className="cookies-consent-btn cookies-consent-btn-outline"
          style={{
            padding: '10px 24px'
          }}
          sx={{
            border: '2px solid #01ABAE',
            color: '#01ABAE',
            background: 'transparent',
            minWidth: '120px',
            '&:hover': {
              backgroundColor: 'rgba(1, 171, 174, 0.04)',
              borderColor: '#009A9D',
            }
          }}
        >
          {s.decline}
        </Button>
        
        <Button 
          onClick={handleAccept}
          variant="contained"
          className="cookies-consent-btn cookies-consent-btn-primary"
          style={{
            padding: '10px 24px'
          }}
          sx={{
            background: '#01ABAE',
            color: 'white',
            minWidth: '120px',
            '&:hover': {
              background: '#01ABAE',
              boxShadow: '0 4px 12px rgba(1, 171, 174, 0.3)',
            }
          }}
        >
          {s.accept}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
